import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { addOverride } from "../../../../../../../scripts/api_v2/overrides_client";
import useBackend from "../../../../../../../hooks/use_backend";
import { COMMISSIONING_PHASE_KEYS } from "../../enum/commissioning_phase_keys";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

const QuickOverrideButton = ({ isDisabled, site_id, device_id, phase_key }) => {
  const { enqueueSnackbar } = useSnackbar();
  const backend = useBackend();
  const curtail = "Trigger curtail override";
  const charge = "Trigger 100% charge override";
  const discharge = "Trigger 100% discharge override";
  const xPercentcharge = "Trigger 20% charge override";
  const xPercentdischarge = "Trigger 20% discharge override";
  const gridFraction = "Trigger grid fraction override";

  const { mutate: doCommitOverride, isPending: isCommitting } = useMutation({
    mutationFn: async ({ overrideType, deviceId }) => {
      const override = getOverride(overrideType, device_id);
      await addOverride(backend.fetch, site_id, override);
    },
    onError: (err, variables, context) => {
      console.error(err);
      enqueueSnackbar("Override add failed", { variant: "error" });
    },
    onSuccess: async () => {
      enqueueSnackbar("Override added for 2 minutes", { variant: "success" });
    },
  });

  function getTs(minutesToAdd) {
    const now = dayjs();
    return now.add(minutesToAdd, "minutes").unix();
  }

  function getOverride(overrideType, device_id) {
    const now = dayjs().unix();
    if (overrideType === curtail) {
      return {
        value: 1,
        override: "CURTAILMENT_PRODUCTION_FRACTION",
        valid_from_ts: now,
        valid_to_ts: getTs(2),
        device_id: device_id,
      };
    } else if (overrideType === gridFraction) {
      return { value: 1, override: "GRID_FRACTION", valid_from_ts: now, valid_to_ts: getTs(2), device_id: null };
    } else if (overrideType === charge) {
      return { value: 1, override: "FLEX_FRACTION", valid_from_ts: now, valid_to_ts: getTs(2), device_id: device_id };
    } else if (overrideType === discharge) {
      return { value: -1, override: "FLEX_FRACTION", valid_from_ts: now, valid_to_ts: getTs(2), device_id: device_id };
    } else if (overrideType === xPercentcharge) {
      return { value: 0.2, override: "FLEX_FRACTION", valid_from_ts: now, valid_to_ts: getTs(2), device_id: device_id };
    } else if (overrideType === xPercentdischarge) {
      return {
        value: -0.2,
        override: "FLEX_FRACTION",
        valid_from_ts: now,
        valid_to_ts: getTs(2),
        device_id: device_id,
      };
    }
  }

  function getOverrideButtons(isDisabled, phase_key, device_id) {
    if (phase_key === COMMISSIONING_PHASE_KEYS.BATTERIES) {
      return (
        <div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            {getOverrideButton(isDisabled, "20% charge override for the next 2 minutes", xPercentcharge, device_id)}
            {getOverrideButton(
              isDisabled,
              "20% discharge override for the next 2 minutes",
              xPercentdischarge,
              device_id,
            )}
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            {getOverrideButton(isDisabled, "Max charge override for the next 2 minutes", charge, device_id)}
            {getOverrideButton(isDisabled, "Max discharge override for the next 2 minutes", discharge, device_id)}
          </div>
        </div>
      );
    } else if (phase_key === COMMISSIONING_PHASE_KEYS.SOLAR_INVERTERS) {
    /*
        else if (phase_key === COMMISSIONING_PHASE_KEYS.GRID_METER)
        {
            return <>
                {getOverrideButton(isDisabled, "Try to reach a grid value of 100% of the setpoints for the next 2 minutes", gridFraction, device_id)}
            </>
        }

         */
      return (
        <>
          {getOverrideButton(isDisabled, "Try to curtail production 100% for the next 2 minutes", curtail, device_id)}
        </>
      );
    }
  }

  function getOverrideButton(isDisabled, tooltip, overrideType, deviceId) {
    return (
      <Tooltip title={tooltip}>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          disabled={isDisabled}
          onClick={() => doCommitOverride({ overrideType: overrideType, deviceId: deviceId })}
          sx={{ borderRadius: 2 }}
          disableElevation
        >
          {overrideType}
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      {phase_key === COMMISSIONING_PHASE_KEYS.METERS ? null : isCommitting ? (
        <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
          {getOverrideButtons(isDisabled, phase_key, device_id)}
        </Box>
      )}
    </>
  );
};

export default QuickOverrideButton;
